import { useState } from "react";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { Button } from "@/components/catalyst/button";
import { Checkbox, CheckboxField } from "@/components/catalyst/checkbox";
import { Label } from "@/components/catalyst/fieldset";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { AlertDialog } from "@/lib/Components/Dialog/AlertDialog";
import {
  AlertActions,
  AlertDescription,
  AlertTitle,
} from "@/components/catalyst/alert";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateSupplierTripMutation } from "@/app/SupplierTrips/GraphQL/supplierTripMutations";
import { UpdateSupplierTripInput } from "@/gql/graphql";
import { localStorageKeys } from "@/lib/utils";

export type TripDefaultChangeItem = {
  id: string;
  label: string;
  from: string;
  to: string;
  field: { key: keyof UpdateSupplierTripInput; value: unknown };
};

export type UpdateTripDefaultsDialogProps = {
  tripId: string;
  items: TripDefaultChangeItem[];
};

function buildInput(items: TripDefaultChangeItem[]): UpdateSupplierTripInput {
  return Object.fromEntries(
    items.map((item) => [item.field.key, item.field.value]),
  );
}

export function UpdateTripDefaultsDialog({
  tripId,
  items,
}: UpdateTripDefaultsDialogProps) {
  const t = useTranslations("relocation.tripDefaultsDialog");
  const { close, resolve, reject } = useDialogState();
  const { mutateAsync } = useGqlMutation(updateSupplierTripMutation);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(items.map((item) => item.id)),
  );

  function toggle(id: string, checked: boolean) {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }

  return (
    <AlertDialog
      size="lg"
      onClose={() => {
        reject?.(false);
      }}
    >
      <AlertTitle>{t("title")}</AlertTitle>
      <AlertDescription>{t("description")}</AlertDescription>
      <div className="mt-4 space-y-3">
        {items.map((item) => (
          <CheckboxField key={item.id}>
            <Checkbox
              checked={selectedIds.has(item.id)}
              onChange={(checked) => toggle(item.id, checked)}
            />
            <Label className="min-w-0">
              <span className="font-medium">{item.label}</span>
              <span className="block break-words text-zinc-600 dark:text-zinc-400">
                {item.from} → {item.to}
              </span>
            </Label>
          </CheckboxField>
        ))}
      </div>
      <AlertActions>
        <button
          type="button"
          className="text-sm text-zinc-500 underline hover:text-zinc-700 sm:mr-auto dark:text-zinc-400 dark:hover:text-zinc-200"
          onClick={() => {
            localStorage.setItem(
              localStorageKeys.SKIP_TRIP_DEFAULTS_PROMPT,
              "true",
            );
            toast(t("dontAskAgainToast"));
            close();
            reject?.(false);
          }}
        >
          {t("dontAskAgain")}
        </button>
        <Button
          outline
          onClick={() => {
            close();
            reject?.(false);
          }}
        >
          {t("keepDefaults")}
        </Button>
        <Button
          isLoading={isLoading}
          disabled={selectedIds.size === 0}
          onClick={async () => {
            try {
              setIsLoading(true);
              await mutateAsync({
                id: tripId,
                input: buildInput(
                  items.filter((item) => selectedIds.has(item.id)),
                ),
              });
              await close();
              resolve?.(true);
            } finally {
              setIsLoading(false);
            }
          }}
        >
          {t("updateDefaults")}
        </Button>
      </AlertActions>
    </AlertDialog>
  );
}
